export interface AttendanceCalculationRule {
  employeeId: string;
  effectiveFrom: string;
  mode: 'minute' | 'default';
}

export interface AttendanceCalculationInput {
  clockIn?: string | null;
  clockOut?: string | null;
  breakMinutes?: number;
  shiftType?: string | null;
  roundUpClockIn?: boolean;
  minuteMode?: boolean;
  baseDate?: string | null;
  clockInDate?: string | null;
  clockOutDate?: string | null;
}

interface ClockValue {
  minutes: number;
  date: string | null;
}

const MINUTES_PER_DAY = 24 * 60;
const JST_OFFSET_MINUTES = 9 * 60;

const dateDay = (value: string | null | undefined): number | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) return null;
  return timestamp / 86400000;
};

/** Resolve the effective-dated exception; a later default rule removes an exception. */
export const resolveMinuteMode = (
  rules: readonly AttendanceCalculationRule[],
  employeeId: string | null | undefined,
  workDate: string | null | undefined
): boolean => {
  if (!employeeId || dateDay(workDate) === null) return false;
  let effectiveRule: AttendanceCalculationRule | undefined;
  rules.forEach(rule => {
    if (
      rule.employeeId === employeeId &&
      dateDay(rule.effectiveFrom) !== null &&
      rule.effectiveFrom <= workDate! &&
      (!effectiveRule || rule.effectiveFrom >= effectiveRule.effectiveFrom)
    ) effectiveRule = rule;
  });
  return effectiveRule?.mode === 'minute';
};

const parseClockValue = (value: string | null | undefined): ClockValue | null => {
  if (!value) return null;
  // ISO punches are converted to the workplace's date/time, independently of the browser timezone.
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
    if (dateDay(value.slice(0, 10)) === null) return null;
    const withOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    if (withOffset) {
      const timestamp = Date.parse(value);
      if (!Number.isFinite(timestamp)) return null;
      const jst = new Date(timestamp + JST_OFFSET_MINUTES * 60000);
      return {
        minutes: jst.getUTCHours() * 60 + jst.getUTCMinutes(),
        date: jst.toISOString().slice(0, 10)
      };
    }
    const time = parseClockValue(value.slice(11));
    return time ? { ...time, date: value.slice(0, 10) } : null;
  }

  const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? 0);
  if (hours > 24 || minutes > 59 || seconds > 59 || (hours === 24 && (minutes !== 0 || seconds !== 0))) {
    return null;
  }
  // Payroll has always used HH:mm; seconds are kept in the source punches, not rounded up.
  return { minutes: hours * 60 + minutes, date: null };
};

const effectiveStartMinutes = (
  minutes: number,
  { roundUpClockIn = true, minuteMode = false }: Pick<AttendanceCalculationInput, 'roundUpClockIn' | 'minuteMode'>
): number => roundUpClockIn && !minuteMode ? Math.ceil(minutes / 30) * 30 : minutes;

/** A display-only value. Never persist this value in place of the original punch. */
export const roundAttendanceClockIn = (
  value: string | undefined,
  options: Pick<AttendanceCalculationInput, 'roundUpClockIn' | 'minuteMode'> = {}
): string | undefined => {
  const parsed = parseClockValue(value);
  if (!parsed) return value;
  const minutes = effectiveStartMinutes(parsed.minutes, options);
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

/** Keep original seconds/offset when a minute-precision editing form did not change the punch. */
export const preserveUnchangedPunchIso = (
  originalIso: string | null | undefined,
  nextDate: string | null | undefined,
  nextTime: string | null | undefined,
  replacementIso: string | null
): string | null => {
  const original = parseClockValue(originalIso);
  const next = parseClockValue(nextTime);
  if (originalIso && original?.date && next && original.date === nextDate && original.minutes === next.minutes) {
    return originalIso;
  }
  return replacementIso;
};

/**
 * Calculate from the original punches. Only clock-in is rounded for default-mode staff.
 * Same-day times can cross midnight; explicitly dated negative spans are invalid.
 * Rounding a short shift past its clock-out produces zero, never a spurious 24-hour shift.
 */
export const calculateAttendanceMinutes = (input: AttendanceCalculationInput): number => {
  const clockIn = parseClockValue(input.clockIn);
  const clockOut = parseClockValue(input.clockOut);
  if (!clockIn || !clockOut) return 0;

  const startDate = input.clockInDate ?? clockIn.date ?? input.baseDate;
  const endDate = input.clockOutDate ?? clockOut.date ?? input.baseDate;
  const startDay = dateDay(startDate);
  const endDay = dateDay(endDate);
  if ((startDate && startDay === null) || (endDate && endDay === null)) return 0;

  const startMinutes = effectiveStartMinutes(clockIn.minutes, input);
  let elapsedMinutes: number;
  if (startDay !== null && endDay !== null) {
    let dayDifference = endDay - startDay;
    if (dayDifference < 0) return 0;
    if (dayDifference === 0 && clockOut.minutes < clockIn.minutes) {
      if (input.clockOutDate || clockOut.date) return 0;
      dayDifference = 1;
    }
    elapsedMinutes = dayDifference * MINUTES_PER_DAY + clockOut.minutes - startMinutes;
  } else {
    const dayDifference = clockOut.minutes < clockIn.minutes ? 1 : 0;
    elapsedMinutes = dayDifference * MINUTES_PER_DAY + clockOut.minutes - startMinutes;
  }
  const breakMinutes = Number.isFinite(input.breakMinutes) ? input.breakMinutes! : 0;
  return Math.max(0, Math.floor(elapsedMinutes) - breakMinutes);
};

/** Preserve the existing monthly/payroll minimum-break rule and saved break adjustments. */
export const calculateEffectiveBreakMinutes = (
  elapsedMinutes: number,
  storedBreakMinutes: number,
  shiftType: string | null | undefined,
  defaults: { dayBreakMinutes: number; nightBreakMinutes: number }
): number => {
  const storedBreak = Number.isFinite(storedBreakMinutes) ? storedBreakMinutes : 0;
  const configured = shiftType === 'night' ? defaults.nightBreakMinutes : defaults.dayBreakMinutes;
  const defaultBreak = Number.isFinite(configured) ? configured : 0;
  return elapsedMinutes >= 480 ? Math.max(storedBreak, defaultBreak) : storedBreak;
};
